"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const SessionGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkStatus = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      // If there's an auth error (like invalid refresh token), clear local session
      if (error) {
        console.error("[SessionGuard] Auth error detected:", error.message);
        if (error.message.includes("refresh_token") || error.message.includes("not found")) {
          await supabase.auth.signOut();
          return;
        }
      }

      if (!session) return;

      // 1. Initial Check: Is user in blocked_users table?
      const { data: blocked } = await supabase
        .from('blocked_users')
        .select('reason')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (blocked) {
        await handleForceLogout((blocked as any).reason);
      }
    };

    checkStatus();

    // 2. Realtime Listener: Watch for immediate blocks
    const channel = supabase
      .channel('public:blocked_users')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'blocked_users',
        },
        async (payload) => {
          if (payload.eventType === 'DELETE') return;
          const { data: { user } } = await supabase.auth.getUser();
          if (user && payload.new.user_id === user.id) {
            await handleForceLogout(payload.new.reason);
          }
        }
      )
      .subscribe();

    // 3. Auth Listener: Handle sign outs and token issues
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear any stale local data if needed
      }
      
      // Catch token refresh failures that happen during the session
      if (event === 'TOKEN_REFRESHED' && !session) {
        await supabase.auth.signOut();
      }
    });

    return () => {
      supabase.removeChannel(channel);
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleForceLogout = async (reason?: string) => {
    await supabase.auth.signOut();
    navigate("/login");
    setTimeout(() => {
        toast.error("החשבון שלך נחסם", {
            description: reason ? `סיבה: ${reason}` : "אנא צור קשר עם מנהל המערכת.",
            duration: 10000,
        });
    }, 500);
  };

  return <>{children}</>;
};